(function () {
  if (window.CartGiftManager) return;

  class CartGiftManager {
    constructor(options = {}) {
      this.cartApi = options.cartApi || window.CartApi;
      this.resolvers = [];
      this.isRunning = false;
      this.currentPromise = null;
      this.pendingOptions = null;
      this.hasRunCartReady = false;
      this.pendingCartReadyEvent = null;

      this.bindCartApiEvents();
    }

    bindCartApiEvents() {
      if (!this.cartApi) return;

      this.cartApi.onCartReady(async ({ cart, options } = {}) => {
        if (this.hasRunCartReady) return null;

        if (!this.resolvers.length) {
          this.pendingCartReadyEvent = { cart, options };
          return null;
        }

        return this.runCartReady(cart, options);
      });

      this.cartApi.onCartItemsMutate(async ({ cart } = {}) => {
        if (!this.resolvers.length) return null;

        return this.run({
          cart,
          forceFetch: !this.cartApi.isValidCart(cart),
          refresh: true,
          refreshOnChangeOnly: true,
          skipNextCartGiftCallback: true,
          source: "cart_items_mutate",
        });
      });
    }

    register(id, resolver) {
      if (!id || typeof resolver !== "function") return;
      this.resolvers = this.resolvers.filter((entry) => entry.id !== id);
      this.resolvers.push({ id, resolver });
      this.runPendingCartReady();
    }

    runPendingCartReady() {
      if (!this.pendingCartReadyEvent || this.hasRunCartReady || !this.resolvers.length) return;

      const { cart, options } = this.pendingCartReadyEvent;
      this.runCartReady(cart, options).catch((error) => {
        console.error("Cart gift ready update failed:", error);
      });
    }

    runCartReady(cart, options = {}) {
      if (this.hasRunCartReady) return Promise.resolve(null);

      this.hasRunCartReady = true;
      this.pendingCartReadyEvent = null;

      return this.run({
        cart,
        forceFetch: !this.cartApi?.isValidCart(cart),
        refresh: false,
        source: options.source || "cart_ready",
      });
    }

    markNextCartCallbackHandled() {
      return this.cartApi?.markNextCartCallbackHandled();
    }

    consumeNextCartCallbackHandled() {
      return !!this.cartApi?.consumeNextCartCallbackHandled();
    }

    async run(options = {}) {
      if (this.isRunning) {
        this.pendingOptions = {
          ...(this.pendingOptions || {}),
          ...options,
          cart: null,
          forceFetch: true,
        };
        return this.currentPromise || Promise.resolve(null);
      }

      this.isRunning = true;
      this.currentPromise = this.runWithLock(options);

      return this.currentPromise;
    }

    async runWithLock(options = {}) {
      try {
        return await this.runLoop(options);
      } finally {
        this.isRunning = false;
        this.currentPromise = null;
      }
    }

    async runLoop(options) {
      let nextOptions = options || {};
      let result = null;

      do {
        this.pendingOptions = null;
        result = await this.runOnce(nextOptions);

        if (this.pendingOptions) {
          nextOptions = {
            ...this.pendingOptions,
            cart: null,
            forceFetch: true,
          };
        }
      } while (this.pendingOptions);

      return result;
    }

    async runOnce(options = {}) {
      if (!this.cartApi) {
        throw new Error("Cart gift manager requires window.CartApi");
      }

      const cart = await this.cartApi.getCart(options);
      if (!this.cartApi.isValidCart(cart)) return null;

      const changes = await this.resolveChanges(cart);
      let updatedCart = null;
      let addedCart = null;

      if (changes.updates.length > 0) {
        updatedCart = await this.cartApi.updateItems(changes.updates, {
          ...options,
          notify: false,
        });
      }

      if (changes.adds.length > 0) {
        addedCart = await this.cartApi.addItems(changes.adds, {
          ...options,
          notify: false,
        });
      }

      if (options.skipNextCartGiftCallback) {
        this.cartApi.markNextCartCallbackHandled();
      }

      const shouldRefresh =
        options.refresh !== false &&
        (!options.refreshOnChangeOnly || updatedCart || addedCart);

      if (shouldRefresh) {
        await this.cartApi.refreshCart(options);
      }

      return addedCart || updatedCart || cart;
    }

    async resolveChanges(cart) {
      const updates = [];
      const adds = [];

      for (const entry of this.resolvers) {
        try {
          const result = await entry.resolver(cart);
          if (!result) continue;

          if (Array.isArray(result.updates)) {
            result.updates.forEach((update) => {
              if (!update) return;
              updates.push(update);
            });
          }

          if (Array.isArray(result.adds)) {
            result.adds.forEach((add) => {
              if (!add) return;
              adds.push(add);
            });
          }
        } catch (error) {
          console.error(`Cart gift resolver "${entry.id}" failed:`, error);
        }
      }

      return {
        updates: this.dedupeUpdates(updates),
        adds: this.dedupeAdds(adds),
      };
    }

    dedupeUpdates(updates) {
      const updatesByTarget = {};

      updates.forEach((update) => {
        const target = update.key || update.id;
        if (!target && target !== 0) return;
        updatesByTarget[String(target)] = update;
      });

      return Object.values(updatesByTarget);
    }

    dedupeAdds(adds) {
      const seen = {};
      const result = [];

      adds.forEach((add) => {
        const variantId = add.id || add.variantId;
        if (!variantId) return;

        const properties = add.properties || {};
        const source = properties.__dreame_gift_source || add.source || "";
        const key = `${source}|${variantId}|${JSON.stringify(properties)}`;

        if (seen[key]) return;
        seen[key] = true;
        const quantity = Number(add.quantity || 1);

        result.push({
          id: Number(variantId),
          quantity: Number.isFinite(quantity) ? Math.max(1, quantity) : 1,
          properties,
        });
      });

      return result;
    }
  }

  window.CartGiftManager = new CartGiftManager({
    cartApi: window.CartApi,
  });
})();
